import React from 'react'
import { Currency, Price } from '@uniswap/sdk-core'
import StepCounter from 'components/InputStepCounter/InputStepCounter'
import { RowBetween } from 'components/Row'
import { useActiveWeb3React } from 'hooks'
import { wrappedCurrency } from 'utils/wrappedCurrency'

import { nearestUsableTick, TickMath } from '@uniswap/v3-sdk'
import { BigNumber } from 'ethers'
import { formatUnits } from 'ethers/lib/utils'
import styled from 'styled-components'

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => theme.bg1};
  border-radius: 16px;
  padding: 6px;
  width: 100%;
  cursor: pointer;
  position: relative;
`

const ToggleOption = styled.div<{ active: boolean }>`
  flex: 1;
  text-align: center;
  padding: 10px 12px;
  font-size: 14px;
  font-weight: ${({ active }) => (active ? '600' : '400')};
  color: ${({ theme, active }) => (active ? theme.text5 : theme.text4)};
  background: ${({ theme, active }) => (active ? theme.primary1 : 'transparent')};
  border-radius: 12px;
  transition: all 0.2s ease-in-out;

  &:hover {
    color: ${({ theme, active }) => (active ? theme.text5 : theme.text3)};
  }
`

// currencyA is the base token
export default function RangeSelector({
  priceLower,
  priceUpper,
  onLeftRangeInput,
  onRightRangeInput,
  getDecrementLower,
  getIncrementLower,
  getDecrementUpper,
  getIncrementUpper,
  currencyA,
  currencyB,
  feeAmount,
  setMaxPriceRange,
}: {
  priceLower?: Price
  priceUpper?: Price
  getDecrementLower: () => string
  getIncrementLower: () => string
  getDecrementUpper: () => string
  getIncrementUpper: () => string
  onLeftRangeInput: (typedValue: string) => void
  onRightRangeInput: (typedValue: string) => void
  currencyA?: Currency | null
  currencyB?: Currency | null
  feeAmount?: number
  setMaxPriceRange?: (isMaxPriceRange: boolean) => void
}) {
  const { chainId } = useActiveWeb3React()
  const [safeMinPrice, setSafeMinPrice] = React.useState('')
  const [safeMaxPrice, setSafeMaxPrice] = React.useState('')
  const [minDecimals, setMinDecimals] = React.useState(18)
  const [maxDecimals, setMaxDecimals] = React.useState(18)

  const tokenA = wrappedCurrency(currencyA ?? undefined, chainId)
  const tokenB = wrappedCurrency(currencyB ?? undefined, chainId)
  const isSorted = tokenA && tokenB && tokenA?.address !== tokenB?.address && tokenA.sortsBefore(tokenB)

  const leftPrice = isSorted ? priceLower : priceUpper?.invert()
  const rightPrice = isSorted ? priceUpper : priceLower?.invert()

  const handleInfiniteRange = React.useCallback(() => {
    if (!currencyA || !currencyB || !feeAmount) return

    const MAX_THRESHOLD = BigNumber.from('10').pow(36)

    const tickSpacing =
      feeAmount === 10000 ? 200 : feeAmount === 3000 ? 60 : feeAmount === 500 ? 10 : feeAmount === 100 ? 1 : 1

    const minTick = nearestUsableTick(TickMath.MIN_TICK, tickSpacing)
    const maxTick = nearestUsableTick(TickMath.MAX_TICK, tickSpacing)

    const sqrtRatioX96Min = TickMath.getSqrtRatioAtTick(minTick)
    const sqrtRatioX96Max = TickMath.getSqrtRatioAtTick(maxTick)

    const sqrtRatioX96MinBN = BigNumber.from(sqrtRatioX96Min.toString())
    const sqrtRatioX96MaxBN = BigNumber.from(sqrtRatioX96Max.toString())

    const currencyASorted = isSorted ? currencyA : currencyB
    const currencyBSorted = isSorted ? currencyB : currencyA

    const minDecimals = Math.min(currencyASorted?.decimals ?? 18, currencyBSorted?.decimals ?? 18)
    const maxDecimals = Math.max(currencyASorted?.decimals ?? 18, currencyBSorted?.decimals ?? 18)

    setMinDecimals(minDecimals)
    setMaxDecimals(maxDecimals)

    const minPrice = sqrtRatioX96MinBN.mul(sqrtRatioX96MinBN).div(BigNumber.from(2).pow(192))
    const maxPrice = sqrtRatioX96MaxBN.mul(sqrtRatioX96MaxBN).div(BigNumber.from(2).pow(192))

    const safeMinPrice = minPrice.isZero()
      ? formatUnits(BigNumber.from(1), minDecimals)
      : formatUnits(minPrice, minDecimals)

    const safeMaxPrice = maxPrice.gte(BigNumber.from('10').pow(18))
      ? formatUnits(MAX_THRESHOLD, maxDecimals)
      : formatUnits(maxPrice.mul(BigNumber.from(999)).div(BigNumber.from(1000)), maxDecimals)

    setSafeMinPrice(safeMinPrice)
    setSafeMaxPrice(safeMaxPrice)

    onLeftRangeInput(safeMinPrice)
    onRightRangeInput(safeMaxPrice)
  }, [currencyA, currencyB, feeAmount, isSorted, onLeftRangeInput, onRightRangeInput])

  const safeMinPriceNumber = parseFloat(safeMinPrice)
  const leftPriceNumber = parseFloat(leftPrice?.toSignificant(6) ?? '0')

  const safeMaxPriceNumber = parseFloat(safeMaxPrice)
  const rightPriceNumber = parseFloat(rightPrice?.toSignificant(6) ?? '0')

  const isMaxPriceRange =
    leftPriceNumber > 0 &&
    Math.abs(safeMinPriceNumber - leftPriceNumber) / leftPriceNumber <= 0.05 &&
    rightPriceNumber > 0 &&
    Math.abs(safeMaxPriceNumber - rightPriceNumber) / rightPriceNumber <= 0.05

  const resetPriceRange = () => {
    onLeftRangeInput('')
    onRightRangeInput('')
  }

  React.useEffect(() => {
    handleInfiniteRange()
  }, [handleInfiniteRange, tokenA, tokenB])

  React.useEffect(() => {
    if (setMaxPriceRange) {
      setMaxPriceRange(isMaxPriceRange)
    }
  }, [isMaxPriceRange, setMaxPriceRange])

  return (
    <>
      <RowBetween>
        <ToggleWrapper>
          <ToggleOption active={isMaxPriceRange} onClick={() => handleInfiniteRange()}>
            Max range
          </ToggleOption>
          <ToggleOption active={!isMaxPriceRange} onClick={() => resetPriceRange()}>
            Custom range
          </ToggleOption>
        </ToggleWrapper>
      </RowBetween>
      <RowBetween>
        <StepCounter
          value={leftPrice?.toSignificant(5) ?? ''}
          onUserInput={onLeftRangeInput}
          width="48%"
          decrement={isSorted ? getDecrementLower : getIncrementUpper}
          increment={isSorted ? getIncrementLower : getDecrementUpper}
          feeAmount={feeAmount}
          label={leftPrice ? `${currencyB?.symbol}` : '-'}
          title={'Min Price'}
          tokenA={currencyA?.symbol}
          tokenB={currencyB?.symbol}
          isMaxPriceRange={isMaxPriceRange}
          maxOrMinDecimals={minDecimals}
        />
        <StepCounter
          value={rightPrice?.toSignificant(5) ?? ''}
          onUserInput={onRightRangeInput}
          width="48%"
          decrement={isSorted ? getDecrementUpper : getIncrementLower}
          increment={isSorted ? getIncrementUpper : getDecrementLower}
          feeAmount={feeAmount}
          label={rightPrice ? `${currencyB?.symbol}` : '-'}
          tokenA={currencyA?.symbol}
          tokenB={currencyB?.symbol}
          title={'Max Price'}
          isMaxPriceRange={isMaxPriceRange}
          maxOrMinDecimals={maxDecimals}
        />
      </RowBetween>
    </>
  )
}
